import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getPublicClient, readContract, readContracts } from '@wagmi/core';
import { encodeFunctionData, getAddress, parseEther } from 'viem';
import { WebStorageService } from './web-storage.service';
import { AppkitService } from './appkit.service';

const collectionAbi = require('src/app/shared/abis/collection.json');
const lazyMintAbi = require('src/app/shared/abis/lazy-mint-nft.json');
const platformFeeAbi = require('src/app/shared/abis/platformFees.json')
const loanFeeAbi = require('src/app/shared/abis/loanfee.json')
const factoryAbi = require('src/app/shared/abis/factory.json')
const nftLoanAbi = require('src/app/shared/abis/nft-loan.json');

@Injectable({
  providedIn: 'root'
})
export class NftContractService {

  publicClient = getPublicClient(this.appkitService.adapter.wagmiConfig);
  protected readonly contractMethod = CONTRACT_METHOD
  constructor(
    private http: HttpClient,
    private webStorageService: WebStorageService,
    private appkitService: AppkitService
  ) { }

  /**
   * Gets nft owner
   * @param collectionAddress
   * @param tokenId
   * @param chainId
   * @returns
   */
  public async getNftOwner(collectionAddress: string, tokenId: number, chainId: number) {
    return await readContract(this.appkitService.adapter.wagmiConfig,{
      address: await getAddress(collectionAddress),
      abi: collectionAbi,
      chainId,
      functionName: 'ownerOf',
      args: [tokenId]
    });
  }


  /**
   * Gets nft meta data by url
   * @param url
   * @returns
   */
  public getNftMetaDataByURL(url: string) {
    return this.http.get(url);
  }

  public async lazyMint({ chainId, walletAddress }: any, { functionName, args, value }: any, collectionAddress: string) {
    let contractParams = {
      abi: lazyMintAbi,
      functionName,
      args
    }
    let requiredGas = await this.publicClient?.estimateContractGas({
      address: await getAddress(collectionAddress),
      account: await getAddress(walletAddress),
      value: value ? parseEther(value.toString()) : 0n,
      ...contractParams
    });
    let orderAbi = await encodeFunctionData({
      ...contractParams
    })
    return { orderAbi, requiredGas }
  }

  async getRoyaltyInfo(collection: string, tokenId: string, price: any) {
    return await readContract(this.appkitService.adapter.wagmiConfig,{
      address: await getAddress(collection),
      abi: collectionAbi,
      functionName: 'royaltyInfo',
      args: [tokenId, price]
    });
  }

  transferNft = async ({ chainId, walletAddress }: any, collectionAddress: string, { functionName, args }: any) => {
    let contractParams = {
      abi: collectionAbi,
      functionName,
      args
    }
    let requiredGas = await this.publicClient?.estimateContractGas({
      address: await getAddress(collectionAddress),
      account: await getAddress(walletAddress),
      ...contractParams
    });
    let transferAbi = await encodeFunctionData({
      ...contractParams
    })
    return { transferAbi, requiredGas }
  }
  /**
   * Params nft contract service
   * @param CONTRACT_ADDRESS 
   * @param functionName 
   * @param [type] 
   * @returns  
   */
  async getPlatFormFeesDetails(CONTRACT_ADDRESS: any, functionName: any, type: number = 1) {
    let abi;
    this.handleAbiFile(type, (abiResult: any) => { abi = abiResult })
    return Promise.resolve(
      (async () => {
        let abiFunction = {
          address: CONTRACT_ADDRESS,
          abi: abi as any,
          functionName: functionName,
          args: []
        }
        const feeDetails = await readContract(this.appkitService.adapter.wagmiConfig,abiFunction);
        return feeDetails
      })()
    );
  }
  /**
   * Params nft contract service
   * @param CONTRACT_ADDRESS 
   * @param functionName 
   * @param type 
   * @returns  
   */
  async getplatformfee(CONTRACT_ADDRESS: any, functionName: string[] | any, type: number) {
    let abi: any;
    this.handleAbiFile(type, (abiResult: any) => { abi = abiResult })
    let _requestArg: any = functionName.length != 1 ? await this.handleaArgtConstruct(functionName, abi, CONTRACT_ADDRESS) : [{ address: CONTRACT_ADDRESS, abi: abi, functionName: functionName[0], args: [] }] //based on the function length construct the request.
    return Promise.resolve(
      (async () => {
        const feeDetails = await readContracts(this.appkitService.adapter.wagmiConfig,{
          contracts: _requestArg
        });
        return feeDetails
      })()
    );
  }

   /**
    * Getplatforms total loan repay fee
    * @param CONTRACT_ADDRESS 
    * @param functionName 
    * @param type 
    * @returns  
    */
   async getplatformTotalLoanRepayFee(CONTRACT_ADDRESS: any, functionName: string[] | any, type: number) {
    let abi: any;
    this.handleAbiFile(type, (abiResult: any) => { abi = abiResult })
    let _requestArg: any = functionName.length != 1 ? await this.handleaArgtConstruct(functionName, abi, CONTRACT_ADDRESS) : [{ address: CONTRACT_ADDRESS, abi: abi, functionName: functionName[0], args: [] }] //based on the function length construct the request.
    return Promise.resolve(
      (async () => {
        const loanRepayFeeDetails = await readContracts(this.appkitService.adapter.wagmiConfig,{
          contracts: _requestArg
        });        
        return loanRepayFeeDetails
      })()
    );
  }

  /**
   * Gets spreadfee
   * @param CONTRACT_ADDRESS 
   * @param functionName 
   * @param type 
   * @returns  
   */
  async getSpreadfee(CONTRACT_ADDRESS: any, functionName: string[] | any, type: number) {
    let abi: any;
    this.handleAbiFile(type, (abiResult: any) => { abi = abiResult })
    let _requestArg: any = functionName.length != 1 ? await this.handleaArgtConstruct(functionName, abi, CONTRACT_ADDRESS) : [{ address: CONTRACT_ADDRESS, abi: abi, functionName: functionName[0], args: [] }] //based on the function length construct the request.
    return Promise.resolve(
      (async () => {
        const spreadfeeFeeDetails = await readContracts(this.appkitService.adapter.wagmiConfig,{
          contracts: _requestArg
        });        
        return spreadfeeFeeDetails
      })()
    );
  }

  /**
   * Gets loan details
   * @param args 
   * @param functionName 
   * @param CONTRACT_ADDRESS 
   * @param type 
   * @returns  
   */
  async getLoanDetails(args: any[], functionName: string, CONTRACT_ADDRESS: any, type: any) {
    let abi: any;
    this.handleAbiFile(type, (abiResult: any) => { abi = abiResult })

    return Promise.resolve(
      (async () => {
        let abiFunction = {
          address: CONTRACT_ADDRESS,
          abi: nftLoanAbi,
          functionName: functionName,
          args
        }
        const result = await readContract(this.appkitService.adapter.wagmiConfig,{
          ...abiFunction
        });
        return result
      })()
    );
  }

  /**
   * Handleas argument function
   * @param functionName 
   * @param abi 
   * @param CONTRACT 
   * @returns  
   */
  async handleaArgtConstruct(functionName: string[], abi: Array<any>, CONTRACT: string | any) { //Construct the contract request argument
    if (functionName.length != 0 && functionName.length != 1) {
      const lenth = functionName.length
      let _contractArg: { address: string, abi: any, functionName: any, args: any }[] = [];
      for (let index = 0; index < lenth; index++) {
        let construct: any = {
          address: CONTRACT,
          abi: abi,
          functionName: functionName[index],
          args: []
        }
        _contractArg.push(construct)
      }
      return _contractArg;
    }
  }

  /**
   * Handles abi file
   * @param CONTRACT_TYPE 
   * @param callBack 
   * @returns  
   */
  handleAbiFile(CONTRACT_TYPE: number, callBack: (abi: any) => void) {
    switch (CONTRACT_TYPE) {
      case this.contractMethod?.EXCHANGE_CONTRACT:
        callBack(platformFeeAbi)
        return
      case this.contractMethod?.LOAN_CONTRACT:
        callBack(loanFeeAbi)
        return;
      case this.contractMethod?.FACTORY_CONTRACT:
        callBack(factoryAbi)
        return;

      default:
        break;
    }
  }

  /**
   * Handles contract address
   * @param type 
   * @param callBack 
   * @returns  
   */
  handleContractAddress(type: number, callBack: (name: string) => void) {
    switch (type) {
      case this.contractMethod?.EXCHANGE_CONTRACT:
        callBack('EXCHANGE_CONTRACT')
        return;
      case this.contractMethod?.LOAN_CONTRACT:
        callBack('BORROW_LEND_CONTRACT')
        return;
      case this.contractMethod?.FACTORY_CONTRACT:
        callBack('FACTORY_ADDRESS')
        return;
      default:
        break;
    }
  }
}

enum CONTRACT_METHOD {
  EXCHANGE_CONTRACT = 1,
  LOAN_CONTRACT = 2,
  FACTORY_CONTRACT = 3
}
